import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import type { TableColumn } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { PortalToContainer } from 'components/PortalToContainer/PortalToContainer';
import { renderCellContentByType } from 'components/Table/cells/renderCellContentByType';
import { useTableContextSelectors } from 'components/Table/model/TableModel';
import { setTableRowDetail } from 'components/Table/model/commands/setTableRowDetail';
import { toggleColumnDetail } from 'components/Table/model/commands/toggleColumnDetail';
import { selectTableCellContext } from 'components/Table/model/selectors/selectTableCellContext';
import { useLocale } from 'containers/App/LocaleContext';
import { useEffect } from 'react';
import { type FC, Fragment, type ReactElement } from 'react';
import './TableRowDetail.css';
import { TableRowDetailTable } from './TableRowDetailTable';
import { useRowDetailData } from './useRowDetailData';

interface Props {
  tableRowDetailContainer?: HTMLElement | null;
}

export const TableRowDetail: FC<Props> = (props) => {
  const {
    table: { rowDetail: rowDetailMsg },
  } = useLocale();
  const ctxProps = useTableContextSelectors({
    dispatch: prop('dispatch'),
    tableRowDetail: prop('tableRowDetail'),
    data: prop('data'),
    tableCellContext: selectTableCellContext,
    snapshotId: prop('snapshotId'),
  });

  // Close row detail on snapshot change and therefore new data are fetched
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally trigger on snapshot change
  useEffect(() => {
    ctxProps.dispatch(setTableRowDetail, { index: null });
  }, [ctxProps.snapshotId]);

  const { columnDetailData } = useRowDetailData(ctxProps.tableRowDetail?.row);

  if (ctxProps.tableRowDetail === null) {
    return null;
  }

  const context = {
    ...ctxProps.tableCellContext,
    detailView: true,
  };

  const renderColumn = (column: TableColumn<any>, index: number) => {
    const detailData = columnDetailData?.[column.key];
    const handleClick = detailData
      ? () => {
          ctxProps.dispatch(toggleColumnDetail, { column: column.key });
        }
      : undefined;

    return (
      <Fragment key={column.key}>
        <div
          className={classNames(
            'flex justify-between gap-2 py-1 pl-2',
            handleClick && 'table-row-detail__clickable',
            ctxProps.tableRowDetail?.expandedColumns.has(column.key) &&
              'table-row-detail__clickable--expanded',
          )}
          onClick={handleClick}
        >
          <Tooltip content={column.description}>
            <span className='text-bluegray-600 text-xs'>{column.title}</span>
          </Tooltip>
          <div className='pr-7 text-right font-semibold'>
            {
              renderCellContentByType(column, {
                row: ctxProps.tableRowDetail!.row,
                context,
                index,
                severity: null,
              }).content
            }
          </div>
          {detailData && (
            <Icon
              className='absolute right-0 text-blue-900'
              size='xs'
              glyph={
                ctxProps.tableRowDetail?.expandedColumns.has(column.key)
                  ? 'fa-chevron-up'
                  : 'fa-chevron-down'
              }
            />
          )}
        </div>
        {detailData &&
          ctxProps.tableRowDetail?.expandedColumns.has(column.key) && (
            <TableRowDetailTable data={detailData} context={context} />
          )}
      </Fragment>
    );
  };

  const maybeWrapWithPortal = (content: ReactElement): ReactElement =>
    props.tableRowDetailContainer ? (
      <PortalToContainer container={props.tableRowDetailContainer}>
        {content}
      </PortalToContainer>
    ) : (
      content
    );

  return maybeWrapWithPortal(
    <div
      className={classNames(
        'table-row-detail',
        props.tableRowDetailContainer && 'flex-1',
      )}
    >
      <div className='table-row-detail__header'>
        <div>{rowDetailMsg.title}</div>
        <Button
          variant='tertiary'
          icon='fa-xmark'
          type='button'
          onClick={() => {
            ctxProps.dispatch(setTableRowDetail, { index: null });
          }}
        />
      </div>
      <div className='flex-1 py-2'>
        <ScrollView>
          {ctxProps.tableRowDetail.columns.map(renderColumn)}
        </ScrollView>
      </div>
    </div>,
  );
};
